/**
 * Weapon definition schema (BUILD_PLAN section 7 damage model). One schema with a block per kind:
 * hitscan, projectile, beam, melee. Numbers are per shot unless stated; beams are per second.
 */
import { z } from 'zod';
const finite = z.number().finite();
const positive = finite.positive();
const nonNegative = finite.nonnegative();
const fraction = finite.min(0).max(1);
export const WeaponKindSchema = z.enum(['hitscan', 'projectile', 'beam', 'melee']);
/** A status effect a weapon applies to whatever it hits (burn, target heat). */
export const OnHitEffectSchema = z
    .object({
    kind: z.string().min(1),
    seconds: positive,
    params: z
        .object({
        speedScale: finite.optional(),
        damageTakenScale: finite.optional(),
        dot: z.object({ damagePerSecond: positive }).strict().optional(),
        heatPerSecond: finite.optional(),
    })
        .strict(),
})
    .strict();
export const WeaponDefinitionSchema = z
    .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    name: z.string().min(1),
    kind: WeaponKindSchema,
    /** Damage per pellet / projectile / melee hit; damage per second for beams. */
    damage: positive,
    /** Shots (or melee attacks) per second; unused by beams. */
    fireRate: positive,
    /** Heat per shot; heat per second for beams. */
    heat: nonNegative,
    /** Heat added to the target per hit (Sparrow's arc pistol). */
    targetHeat: nonNegative.default(0),
    range: z
        .object({
        /** Distance the bot tries to fight at. */
        optimal: positive,
        /** Full damage up to here. */
        falloffStart: nonNegative,
        /** Hitscan stops here; projectiles and beams reach their minimum damage factor here. */
        max: positive,
        minDamageFactor: fraction,
    })
        .strict(),
    magazine: z
        .object({ size: z.number().int().positive(), reloadSeconds: positive })
        .strict()
        .optional(),
    burst: z
        .object({ count: z.number().int().min(2), intervalSeconds: positive })
        .strict()
        .optional(),
    /** Seconds of trigger hold before the weapon reaches its full fire rate (gatling). */
    spinUpSeconds: nonNegative.default(0),
    /** Holding the secondary trigger zooms the camera (sniper-class weapons). */
    zoom: z.boolean().default(false),
    /** Camera kick per shot, degrees (presentation only). */
    recoil: nonNegative.default(0),
    /** Hitting a cloaked mech reveals it (Oracle's beam rifle). */
    revealsCloak: z.boolean().default(false),
    /** Muzzle offset from the mech's feet, in its local frame (metres right, up, forward). */
    muzzle: z
        .object({ right: finite, up: finite, forward: finite })
        .strict()
        .default({ right: 0.7, up: 3.6, forward: 1.4 }),
    onHit: z.array(OnHitEffectSchema).default([]),
    hitscan: z
        .object({
        pellets: z.number().int().positive().default(1),
        /** Base cone half-angle, degrees. */
        spreadDeg: nonNegative,
        bloomPerShot: fraction.default(0),
        bloomDecayPerSecond: nonNegative.default(1),
        /** Extra cone half-angle at full bloom, degrees. */
        maxBloomDeg: nonNegative.default(0),
    })
        .strict()
        .optional(),
    projectile: z
        .object({
        speed: positive,
        /** Downward acceleration, m/s^2 (0 for rockets and energy bolts). */
        gravity: nonNegative.default(0),
        /** Collision radius, metres. */
        radius: positive,
        lifeSeconds: positive,
        splash: z
            .object({
            radius: positive,
            damage: positive,
            /** Damage factor at the edge of the radius. */
            minFactor: fraction.default(0.3),
            selfDamage: z.boolean().default(false),
        })
            .strict()
            .optional(),
        homing: z
            .object({
            /** Turn rate toward the target, degrees per second. */
            turnRateDeg: positive,
            /** Targets are acquired within this cone half-angle of the launch direction. */
            acquireDeg: positive,
        })
            .strict()
            .optional(),
        chain: z
            .object({
            range: positive,
            damageFactor: fraction,
            maxJumps: z.number().int().positive(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
    beam: z
        .object({
        /** Hit radius around the beam line, metres. */
        width: positive,
        /** Longest continuous activation before a forced cool; null for no limit. */
        maxSeconds: positive.nullable().default(null),
        coolSeconds: nonNegative.default(0),
        warmupSeconds: nonNegative.default(0),
        /** Passes through barriers. */
        pierce: z.boolean().default(false),
    })
        .strict()
        .optional(),
    melee: z
        .object({
        reach: positive,
        arcDeg: positive,
        windupSeconds: nonNegative,
        activeSeconds: positive,
        recoverySeconds: nonNegative,
        /** Horizontal knockback speed, m/s. */
        knockback: nonNegative.default(0),
        /** Vertical knock-up speed, m/s. */
        knockUp: nonNegative.default(0),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((w, ctx) => {
    if (w[w.kind] === undefined) {
        ctx.addIssue({ code: 'custom', message: `weapon "${w.id}" needs a "${w.kind}" block` });
    }
    if (w.range.falloffStart > w.range.max) {
        ctx.addIssue({ code: 'custom', message: `weapon "${w.id}": falloffStart exceeds max` });
    }
});
//# sourceMappingURL=weapon-schema.js.map